/* =========================================================================
   Trade-In Landing Page — behaviour for all `ti-*` sections.
   Every section renders this file, so it guards against running twice.
   Components initialise from data attributes and re-initialise when a
   section is added or edited in the Shopify theme editor.
   ========================================================================= */
(function () {
  'use strict';
  if (window.TradeInPage) return;

  var doc = document;
  var root = doc.documentElement;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);
  root.classList.add('ti-js');

  function $all(sel, scope) { return Array.prototype.slice.call((scope || doc).querySelectorAll(sel)); }
  // Marks an element as initialised. Uses a separate `…Init` key so it never
  // collides with config attributes such as data-ti-rail="0.5".
  function once(el, key) {
    var flag = key + 'Init';
    if (el.dataset[flag]) return false;
    el.dataset[flag] = '1';
    return true;
  }

  /* ---------- Reveal on scroll -------------------------------------------- */
  var revealIO = 'IntersectionObserver' in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealIO.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' })
    : null;

  function initReveal(scope) {
    $all('.ti-reveal', scope).forEach(function (el) {
      if (!once(el, 'tiReveal')) return;
      if (!revealIO || designMode) el.classList.add('is-visible');
      else revealIO.observe(el);
    });
  }

  /* ---------- Count-up numbers -------------------------------------------- */
  function initCounters(scope) {
    $all('[data-ti-count]', scope).forEach(function (el) {
      if (!once(el, 'tiCount')) return;
      var match = el.textContent.trim().match(/^([^\d]*)([\d.,]+)(.*)$/);
      if (!match || reduceMotion || designMode || !('IntersectionObserver' in window)) return;
      var prefix = match[1];
      var raw = match[2];
      var suffix = match[3];
      var target = parseFloat(raw.replace(/,/g, ''));
      var decimals = (raw.split('.')[1] || '').length;
      var useComma = raw.indexOf(',') > -1;
      var io = new IntersectionObserver(function (entries) {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        var start = performance.now();
        var duration = 1400;
        (function tick(now) {
          var p = Math.min(1, (now - start) / duration);
          var val = target * (1 - Math.pow(1 - p, 3));
          var str = val.toFixed(decimals);
          if (useComma) str = Number(str).toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
          el.textContent = prefix + str + suffix;
          if (p < 1) requestAnimationFrame(tick);
        })(start);
      }, { threshold: 0.4 });
      el.textContent = prefix + (0).toFixed(decimals) + suffix;
      io.observe(el);
    });
  }

  /* ---------- Sticky ribbon ----------------------------------------------- */
  function initRibbon(scope) {
    $all('[data-ti-ribbon]', scope).forEach(function (ribbon) {
      if (!once(ribbon, 'tiRibbon')) return;
      function update() {
        var hero = doc.querySelector('[data-ti-hero]');
        var trigger = hero ? hero.getBoundingClientRect().bottom : 600 - window.scrollY;
        ribbon.classList.toggle('is-visible', trigger < 0);
      }
      window.addEventListener('scroll', update, { passive: true });
      update();
    });
  }

  /* ---------- Hero image cross-fade ---------------------------------------- */
  function initHeroImages(scope) {
    $all('[data-ti-cycle]', scope).forEach(function (stack) {
      if (!once(stack, 'tiCycle')) return;
      var imgs = $all('.ti-hero__img', stack);
      if (imgs.length < 2 || reduceMotion) return;
      var i = 0;
      var delay = parseInt(stack.getAttribute('data-ti-cycle'), 10) || 3500;
      setInterval(function () {
        imgs[i].classList.remove('is-active');
        i = (i + 1) % imgs.length;
        imgs[i].classList.add('is-active');
      }, delay);
    });
  }

  /* ---------- Trade-in calculator ------------------------------------------ */
  var selection = { source: '', target: '', credit: '' };

  function initCalculator(scope) {
    $all('[data-ti-calc]', scope).forEach(function (calc) {
      if (!once(calc, 'tiCalc')) return;
      var creditEl = calc.querySelector('[data-ti-credit]');
      var targetEl = calc.querySelector('[data-ti-target-name]');

      function refresh() {
        var src = calc.querySelector('[data-ti-source][aria-pressed="true"]');
        var tgt = calc.querySelector('[data-ti-target][aria-pressed="true"]');
        selection.source = src ? src.getAttribute('data-ti-source') : '';
        selection.target = tgt ? tgt.getAttribute('data-ti-target') : '';
        selection.credit = src ? src.getAttribute('data-ti-credit-value') || '' : '';
        if (creditEl) {
          creditEl.textContent = selection.credit;
          creditEl.parentNode.hidden = !selection.credit;
        }
        if (targetEl) targetEl.textContent = selection.target;
        calc.classList.toggle('is-complete', !!(src && tgt));
        doc.dispatchEvent(new CustomEvent('ti:selection', { detail: selection }));
      }

      calc.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ti-source], [data-ti-target]');
        if (!btn || !calc.contains(btn)) return;
        var attr = btn.hasAttribute('data-ti-source') ? 'data-ti-source' : 'data-ti-target';
        $all('[' + attr + ']', calc).forEach(function (b) { b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'); });
        refresh();
      });
    });
  }

  /* ---------- Testimonial rail (auto-scroll + click to play) --------------- */
  function initRails(scope) {
    $all('[data-ti-rail]', scope).forEach(function (rail) {
      if (!once(rail, 'tiRail')) return;
      var track = rail.querySelector('.ti-rail__track');
      var speed = parseFloat(rail.getAttribute('data-ti-rail')) || 0.45;
      var hovering = false;
      var holdUntil = 0;
      var playing = false;
      var pos = 0;

      // Duplicate the cards once so the loop is seamless.
      var originals = $all('.ti-tcard', track);
      if (originals.length > 1 && !reduceMotion && !designMode) {
        originals.forEach(function (card) {
          var clone = card.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          $all('button, a', clone).forEach(function (el) { el.tabIndex = -1; });
          track.appendChild(clone);
        });
      }

      rail.addEventListener('mouseenter', function () { hovering = true; });
      rail.addEventListener('mouseleave', function () { hovering = false; });
      rail.addEventListener('touchstart', function () { holdUntil = Date.now() + 4000; }, { passive: true });
      rail.addEventListener('focusin', function () { holdUntil = Date.now() + 6000; });
      rail.addEventListener('wheel', function () { holdUntil = Date.now() + 2500; pos = rail.scrollLeft; }, { passive: true });

      rail.addEventListener('click', function (e) {
        var thumb = e.target.closest('[data-ti-yt]');
        if (!thumb || thumb.classList.contains('is-playing')) return;
        $all('.is-playing', rail).forEach(resetThumb);
        var iframe = doc.createElement('iframe');
        iframe.src = 'https://www.youtube-nocookie.com/embed/' + thumb.getAttribute('data-ti-yt') + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        iframe.title = thumb.getAttribute('aria-label') || 'Customer video';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        thumb.dataset.tiHtml = thumb.innerHTML;
        thumb.innerHTML = '';
        thumb.appendChild(iframe);
        thumb.classList.add('is-playing');
        playing = true;
      });

      function resetThumb(thumb) {
        if (thumb.dataset.tiHtml != null) thumb.innerHTML = thumb.dataset.tiHtml;
        thumb.classList.remove('is-playing');
      }

      if (reduceMotion || designMode || originals.length < 2) return;
      (function loop() {
        var half = track.scrollWidth / 2;
        if (!hovering && !playing && Date.now() > holdUntil && half > rail.clientWidth) {
          pos += speed;
          if (pos >= half) pos -= half;
          rail.scrollLeft = pos;
        } else {
          pos = rail.scrollLeft;
        }
        requestAnimationFrame(loop);
      })();
    });
  }

  /* ---------- Video facades (YouTube / Vimeo / Shopify-hosted) ------------- */
  function initVideos(scope) {
    $all('[data-ti-embed]', scope).forEach(function (btn) {
      if (!once(btn, 'tiEmbed')) return;
      btn.addEventListener('click', function () {
        var type = btn.getAttribute('data-ti-embed');
        var id = btn.getAttribute('data-ti-id');
        var src = type === 'vimeo'
          ? 'https://player.vimeo.com/video/' + id + '?autoplay=1&dnt=1'
          : 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
        var iframe = doc.createElement('iframe');
        iframe.src = src;
        iframe.title = btn.getAttribute('aria-label') || 'Video';
        iframe.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        btn.replaceWith(iframe);
      });
    });

    $all('[data-ti-play-video]', scope).forEach(function (wrap) {
      if (!once(wrap, 'tiPlay')) return;
      wrap.addEventListener('click', function () {
        var video = wrap.querySelector('video');
        if (!video || wrap.classList.contains('is-playing')) return;
        wrap.classList.add('is-playing');
        var play = wrap.querySelector('.ti-play');
        if (play) play.remove();
        video.controls = true;
        video.muted = false;
        video.play();
      });
    });
  }

  /* ---------- Feature slider ---------------------------------------------- */
  function initSliders(scope) {
    $all('[data-ti-slider]', scope).forEach(function (slider) {
      if (!once(slider, 'tiSlider')) return;
      var track = slider.querySelector('.ti-slider__track');
      var slides = $all('.ti-slide', track);
      var prev = slider.querySelector('[data-ti-prev]');
      var next = slider.querySelector('[data-ti-next]');
      var dots = $all('[data-ti-dot]', slider.parentNode);
      var index = 0;

      function go(i) {
        index = Math.max(0, Math.min(slides.length - 1, i));
        track.style.transform = 'translate3d(' + (-100 * index) + '%,0,0)';
        slides.forEach(function (s, n) {
          s.setAttribute('aria-hidden', n === index ? 'false' : 'true');
          $all('a, button', s).forEach(function (el) { el.tabIndex = n === index ? 0 : -1; });
        });
        dots.forEach(function (d, n) { d.setAttribute('aria-current', n === index ? 'true' : 'false'); });
        if (prev) prev.disabled = index === 0;
        if (next) next.disabled = index === slides.length - 1;
      }

      if (prev) prev.addEventListener('click', function () { go(index - 1); });
      if (next) next.addEventListener('click', function () { go(index + 1); });
      dots.forEach(function (d, n) { d.addEventListener('click', function () { go(n); }); });
      slider.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowLeft') go(index - 1);
        if (e.key === 'ArrowRight') go(index + 1);
      });

      var startX = 0;
      track.addEventListener('touchstart', function (e) { startX = e.changedTouches[0].clientX; }, { passive: true });
      track.addEventListener('touchend', function (e) {
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 50) go(index + (dx < 0 ? 1 : -1));
      }, { passive: true });

      slider._tiGo = go;
      go(0);
    });
  }

  /* ---------- Before / after compare -------------------------------------- */
  function initCompare(scope) {
    $all('[data-ti-ba]', scope).forEach(function (ba) {
      if (!once(ba, 'tiBa')) return;
      var range = ba.querySelector('.ti-ba__range');
      function set() { ba.style.setProperty('--pos', range.value + '%'); }
      range.addEventListener('input', set);
      set();
    });
  }

  /* ---------- Assessment modal -------------------------------------------- */
  var modal = null;

  function initModal(scope) {
    var el = (scope.matches && scope.matches('[data-ti-modal]')) ? scope : scope.querySelector('[data-ti-modal]');
    if (!el || !once(el, 'tiModal')) return;
    // In the theme editor a re-rendered section brings a fresh copy; drop the old one.
    if (modal && modal !== el && modal._tiHolder) modal._tiHolder.remove();
    modal = el;
    // Move the modal to <body> so no parent transform/overflow can clip it.
    var holder = doc.createElement('div');
    holder.className = 'ti';
    holder.appendChild(el);
    doc.body.appendChild(holder);
    el._tiHolder = holder;

    var form = el.querySelector('form');
    var steps = $all('[data-ti-step]', el);
    var questions = steps.filter(function (s) { return s.hasAttribute('data-ti-question'); });
    var contactStep = el.querySelector('[data-ti-contact]');
    var successStep = el.querySelector('[data-ti-success]');
    var flow = questions.concat(contactStep ? [contactStep] : []);
    var countEl = el.querySelector('[data-ti-count-label]');
    var bar = el.querySelector('.ti-modal__bar');
    var back = el.querySelector('[data-ti-back]');
    var next = el.querySelector('[data-ti-next]');
    var nextLabel = next ? next.textContent : 'Next';
    var submitLabel = next ? next.getAttribute('data-ti-submit-label') || 'Submit' : 'Submit';
    var pick = el.querySelector('[data-ti-pick]');
    var current = 0;
    var lastFocus = null;

    function setHidden(name, value) {
      var input = form.querySelector('[data-ti-field="' + name + '"]');
      if (input) input.value = value;
    }

    function stepValid(step) {
      if (step === contactStep) {
        return $all('[required]', step).every(function (f) { return f.value.trim() !== '' && f.checkValidity(); });
      }
      return !!step.querySelector('[aria-pressed="true"]');
    }

    function show(i) {
      current = Math.max(0, Math.min(flow.length - 1, i));
      steps.forEach(function (s) { s.classList.remove('is-active'); });
      flow[current].classList.add('is-active');
      el.classList.remove('is-done');
      if (countEl) countEl.textContent = countEl.getAttribute('data-template').replace('[n]', current + 1).replace('[total]', flow.length);
      if (bar) bar.style.width = ((current + 1) / flow.length * 100) + '%';
      if (back) back.style.visibility = current === 0 ? 'hidden' : 'visible';
      if (next) {
        var isLast = flow[current] === contactStep;
        next.textContent = isLast ? submitLabel : nextLabel;
        next.disabled = !stepValid(flow[current]);
      }
      var body = el.querySelector('.ti-modal__body');
      if (body) body.scrollTop = 0;
    }

    function showSuccess() {
      steps.forEach(function (s) { s.classList.remove('is-active'); });
      if (successStep) successStep.classList.add('is-active');
      el.classList.add('is-done');
      if (bar) bar.style.width = '100%';
    }

    function open(startAt) {
      lastFocus = doc.activeElement;
      el.classList.add('is-open');
      el.setAttribute('aria-hidden', 'false');
      root.classList.add('ti-lock');
      if (startAt === 'success') showSuccess();
      else show(typeof startAt === 'number' ? startAt : 0);
      setTimeout(function () {
        var focusable = el.querySelector('.is-active button, .is-active input, [data-ti-close]');
        if (focusable) focusable.focus();
      }, 50);
    }

    function close() {
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      root.classList.remove('ti-lock');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    function choosePick(name) {
      if (!pick) return;
      $all('[data-ti-pick-option]', pick).forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-ti-pick-option') === name ? 'true' : 'false');
      });
      var nameEl = pick.querySelector('[data-ti-pick-name]');
      if (nameEl) nameEl.textContent = name || nameEl.getAttribute('data-empty');
      pick.classList.toggle('is-choosing', !name);
      setHidden('product', name);
    }

    // Choice buttons inside question steps.
    el.addEventListener('click', function (e) {
      var choice = e.target.closest('[data-ti-choice]');
      if (choice) {
        var step = choice.closest('[data-ti-step]');
        var multi = step.hasAttribute('data-ti-multi');
        if (multi) {
          choice.setAttribute('aria-pressed', choice.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
        } else {
          $all('[data-ti-choice]', step).forEach(function (b) { b.setAttribute('aria-pressed', b === choice ? 'true' : 'false'); });
        }
        var picked = $all('[data-ti-choice][aria-pressed="true"]', step).map(function (b) { return b.getAttribute('data-ti-choice'); });
        setHidden('q' + step.getAttribute('data-ti-question'), picked.join(', '));
        if (next) next.disabled = !stepValid(step);
        if (!multi && flow[current] === step) setTimeout(function () { show(current + 1); }, 260);
        return;
      }
      var option = e.target.closest('[data-ti-pick-option]');
      if (option) { choosePick(option.getAttribute('data-ti-pick-option')); if (next) next.disabled = !stepValid(contactStep); return; }
      if (e.target.closest('[data-ti-pick-change]')) { pick.classList.toggle('is-choosing'); return; }
      var addon = e.target.closest('[data-ti-addon]');
      if (addon) {
        var on = addon.getAttribute('aria-pressed') !== 'true';
        addon.setAttribute('aria-pressed', on ? 'true' : 'false');
        setHidden('addon', on ? 'Yes' : 'No');
        return;
      }
      if (e.target === el || e.target.closest('[data-ti-close]')) close();
    });

    if (contactStep) {
      contactStep.addEventListener('input', function () { if (next && flow[current] === contactStep) next.disabled = !stepValid(contactStep); });
    }
    if (back) back.addEventListener('click', function () { show(current - 1); });
    if (next) next.addEventListener('click', function () {
      if (flow[current] !== contactStep) { show(current + 1); return; }
      if (!stepValid(contactStep)) return;
      var code = form.querySelector('[data-ti-dial]');
      var phone = form.querySelector('[data-ti-phone]');
      if (code && phone) setHidden('phone_full', code.value + ' ' + phone.value);
      next.disabled = true;
      if (form.requestSubmit) form.requestSubmit(); else form.submit();
    });

    function onKey(e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab') {
        var f = $all('button:not([disabled]), input, select, textarea, a[href]', el).filter(function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        if (e.shiftKey && doc.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && doc.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      }
    }

    // Carry the calculator choice into the form.
    function select(sel) {
      setHidden('source', sel.source);
      setHidden('target', sel.target);
      if (sel.target) choosePick(sel.target);
    }

    choosePick('');
    if (selection.target) select(selection);
    el._ti = { open: open, close: close, key: onKey, select: select };

    // After Shopify redirects back from the contact form.
    if (el.querySelector('[data-ti-state="posted"]')) open('success');
    else if (el.querySelector('[data-ti-state="errors"]')) open(flow.length - 1);
  }

  // Page-level listeners are registered once and talk to whichever modal is current.
  doc.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-ti-open]');
    if (!trigger || !modal) return;
    e.preventDefault();
    modal._ti.open(0);
  });
  doc.addEventListener('keydown', function (e) {
    if (modal && modal.classList.contains('is-open')) modal._ti.key(e);
  });
  doc.addEventListener('ti:selection', function (e) {
    if (modal && modal._ti) modal._ti.select(e.detail);
  });

  /* ---------- Boot ---------------------------------------------------------- */
  function initAll(scope) {
    scope = scope || doc;
    initReveal(scope);
    initCounters(scope);
    initRibbon(scope);
    initHeroImages(scope);
    initCalculator(scope);
    initRails(scope);
    initVideos(scope);
    initSliders(scope);
    initCompare(scope);
    initModal(scope === doc ? doc.body : scope);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { initAll(doc); });
  else initAll(doc);

  // Theme editor support.
  doc.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  doc.addEventListener('shopify:block:select', function (e) {
    var slide = e.target.closest('.ti-slide');
    var slider = slide && slide.closest('[data-ti-slider]');
    if (slider && slider._tiGo) slider._tiGo($all('.ti-slide', slider).indexOf(slide));
  });

  doc.addEventListener('shopify:section:unload', function (e) {
    if (modal && modal._tiHolder && e.target.querySelector('[data-ti-modal-anchor]')) {
      modal._tiHolder.remove();
      modal = null;
    }
  });

  window.TradeInPage = { init: initAll, open: function () { if (modal) modal._ti.open(0); } };
})();
